/**
 * Pure SQL builder for chart / KPI / table queries.
 *
 * Lives in `engine/` (not the worker) so the main thread can show a
 * preview of the SQL behind a widget — see the "SQL göster" toggle in
 * `WidgetSettingsRightPanel`. The worker imports the same function for
 * actual execution, so what the user sees and what runs are identical.
 */

import type { QuerySpec } from '@/types/query';
import { escId, buildCondition } from './sql-utils';
import { buildTransformExpr } from './value-transforms';

/**
 * Build a SELECT statement for `spec`. Returns the raw SQL string —
 * no execution, no escaping beyond identifier quoting.
 *
 * Precedence for column expressions: explicit transform (mapping/bin)
 * wins over date bucketing, which wins over the plain identifier.
 */
export function buildSql(spec: QuerySpec): string {
  const parts: string[] = ['SELECT'];
  const buckets = spec.groupByBuckets;
  const transforms = spec.transforms;

  const groupExpr = (col: string): string => {
    const t = transforms?.[col];
    if (t) return buildTransformExpr(t);
    const b = buckets?.[col];
    if (!b) return escId(col);
    return `DATE_TRUNC('${b}', ${escId(col)})`;
  };

  const filterColExpr = (col: string): string => {
    const t = transforms?.[col];
    return t ? buildTransformExpr(t) : escId(col);
  };

  const selectCols: string[] = [];
  for (const col of spec.groupBy) {
    const expr = groupExpr(col);
    selectCols.push(expr === escId(col) ? expr : `${expr} AS ${escId(col)}`);
  }
  for (const m of spec.metrics) {
    const agg = m.aggregation.toUpperCase();
    if (agg === 'COUNT_DISTINCT') {
      selectCols.push(`COUNT(DISTINCT ${escId(m.column)}) AS ${escId(m.alias)}`);
    } else if (agg === 'MEDIAN') {
      selectCols.push(`MEDIAN(${escId(m.column)}) AS ${escId(m.alias)}`);
    } else {
      selectCols.push(`${agg}(${escId(m.column)}) AS ${escId(m.alias)}`);
    }
  }
  if (selectCols.length === 0) selectCols.push('*');
  parts.push(selectCols.join(', '));

  parts.push(`FROM ${escId(spec.source)}`);

  if (spec.filters.length > 0) {
    const conditions = spec.filters.map((f) => buildCondition(f, filterColExpr));
    parts.push('WHERE ' + conditions.join(' AND '));
  }

  if (spec.groupBy.length > 0) {
    parts.push('GROUP BY ' + spec.groupBy.map(groupExpr).join(', '));
  }

  if (spec.sort && spec.sort.length > 0) {
    const sortCols = spec.sort.map(
      (s) => `${escId(s.column)} ${s.direction.toUpperCase()}`,
    );
    parts.push('ORDER BY ' + sortCols.join(', '));
  }

  if (spec.limit != null && spec.limit > 0) {
    parts.push(`LIMIT ${Math.floor(spec.limit)}`);
  }

  return parts.join(' ');
}

/**
 * Pretty-print SQL for the dev / debug UI: insert line breaks before
 * each major clause and indent the projected columns. Purely cosmetic;
 * the original string still parses identically.
 */
export function formatSqlForDisplay(sql: string): string {
  return sql
    .replace(/\s+FROM\b/, '\nFROM')
    .replace(/\s+WHERE\b/, '\nWHERE')
    .replace(/\s+GROUP BY\b/, '\nGROUP BY')
    .replace(/\s+ORDER BY\b/, '\nORDER BY')
    .replace(/\s+LIMIT\b/, '\nLIMIT')
    .replace(/^SELECT /, 'SELECT\n  ')
    .replace(/, /g, ',\n  ');
}
